import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";import {
  Users, Flame, Clock, MapPin, CheckCircle2, XCircle, MessageCircle, Star,
  LogOut, TrendingUp, DollarSign, Award, Video, Phone, ChevronRight, X,
  BadgeCheck, ShieldCheck, Sparkles, RefreshCcw, Info, UserPlus, Copy, Check,
  Send, Globe,
} from "lucide-react";
import GlobalLocationSearch from "@/components/GlobalLocationSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRO_TOKEN_KEY = "altiora_pro_token";
const PRO_KEY_KEY = "altiora_pro_key";

// Dedicated axios instance for pro sessions — different auth header from user JWT
const proApi = axios.create({ baseURL: API });
proApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(PRO_TOKEN_KEY);
  if (token) config.headers["X-Altiora-Pro"] = token;
  return config;
});

const BAND_STYLES = {
  critical: { border: "border-[#DC2626]/40", bg: "bg-[#FEF2F2]", ring: "bg-[#DC2626]", chip: "text-[#991B1B] bg-[#FEE2E2]", label: "Critique" },
  high:     { border: "border-[#F59E0B]/50", bg: "bg-[#FEF7EC]", ring: "bg-[#F59E0B]", chip: "text-[#B45309] bg-[#FEF3C7]", label: "Élevée" },
  followup: { border: "border-[#C5A880]/50", bg: "bg-[#FBF6EA]", ring: "bg-[#C5A880]", chip: "text-[#7A5B14] bg-[#FBF6EA]", label: "Suivi" },
  info:     { border: "border-[#059669]/40", bg: "bg-[#ECFDF5]", ring: "bg-[#059669]", chip: "text-[#065F46] bg-[#D1FAE5]", label: "Info" },
};

const MODALITY_ICON = { video: Video, phone: Phone, in_person: MapPin };

export default function ProDashboard() {
  const [pro, setPro] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("inbox");
  const [selected, setSelected] = useState(null); // booking under action
  const [action, setAction] = useState(null); // "confirm" | "counter" | "decline"
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [me, inb, st] = await Promise.all([
        proApi.get("/partner-pro/me"),
        proApi.get("/partner-pro/inbox"),
        proApi.get("/partner-pro/stats"),
      ]);
      setPro(me.data);
      setInbox(inb.data);
      setStats(st.data);
    } catch (err) {
      // Not authenticated — redirect
      navigate("/pro/login");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem(PRO_TOKEN_KEY)) {
      navigate("/pro/login");
      return;
    }
    load();
  }, []);

  const logout = () => {
    localStorage.removeItem(PRO_TOKEN_KEY);
    localStorage.removeItem(PRO_KEY_KEY);
    navigate("/pro/login");
  };

  if (loading || !pro) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] flex items-center justify-center text-[#5B5B62]">
        <div className="altiora-tracing h-1 w-40 rounded-full" />
      </div>
    );
  }

  const counts = inbox?.counts || { critical: 0, high: 0, followup: 0, info: 0 };
  const items = (inbox?.items || []).filter((b) => filter === "all" || b.criticality.band === filter);

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]">
      {/* Header */}
      <header className="border-b border-[#E4E1D8] bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#7A5B14]">
              ALTIORA · Espace Partenaire Pro
            </div>
            <div className="mt-0.5 font-display text-lg text-[#1F2937] tracking-tight truncate">
              {pro.first_name} {pro.last_name} · <span className="text-[#5B5B62]">{pro.title_fr}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full border border-[#C5A880]/40 bg-[#FBF6EA] px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                <Star className="h-3 w-3 fill-[#C5A880]" strokeWidth={1.5} /> {pro.rating?.toFixed(1)} · {pro.reviews_count}
              </span>
              {pro.verified && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#059669]/40 bg-[#ECFDF5] px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-[#065F46]">
                  <BadgeCheck className="h-3 w-3" strokeWidth={2} /> Vérifié
                </span>
              )}
            </div>
            <button onClick={load} className="p-2 text-[#5B5B62] hover:text-[#1F2937]" title="Rafraîchir">
              <RefreshCcw className="h-4 w-4" strokeWidth={1.8} />
            </button>
            <button
              data-testid="pro-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-xs text-[#5B5B62] hover:text-[#DC2626] altiora-transition"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.8} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Tabs + KPIs */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <BandKpi emoji="🔴" label="Critique" value={counts.critical} accent="text-[#DC2626]" onClick={() => { setFilter("critical"); setTab("inbox"); }} active={filter === "critical"} testId="kpi-critical" />
          <BandKpi emoji="🟠" label="Élevée" value={counts.high} accent="text-[#B45309]" onClick={() => { setFilter("high"); setTab("inbox"); }} active={filter === "high"} testId="kpi-high" />
          <BandKpi emoji="🟡" label="Suivi" value={counts.followup} accent="text-[#7A5B14]" onClick={() => { setFilter("followup"); setTab("inbox"); }} active={filter === "followup"} testId="kpi-followup" />
          <BandKpi emoji="🟢" label="Info" value={counts.info} accent="text-[#065F46]" onClick={() => { setFilter("info"); setTab("inbox"); }} active={filter === "info"} testId="kpi-info" />
          <BandKpi icon={Sparkles} label="Total" value={inbox?.total || 0} accent="text-[#0B2545]" onClick={() => { setFilter("all"); setTab("inbox"); }} active={filter === "all"} testId="kpi-total" />
        </div>

        <div className="inline-flex rounded-md border border-[#E4E1D8] bg-white overflow-hidden">
          <TabBtn active={tab === "inbox"} onClick={() => setTab("inbox")} testId="pro-tab-inbox">
            <Flame className="h-3.5 w-3.5" strokeWidth={2} /> File d'attente ({items.length})
          </TabBtn>
          <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} testId="pro-tab-stats">
            <TrendingUp className="h-3.5 w-3.5" strokeWidth={2} /> Statistiques
          </TabBtn>
          <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} testId="pro-tab-profile">
            <Award className="h-3.5 w-3.5" strokeWidth={2} /> Profil
          </TabBtn>
          <TabBtn active={tab === "invite"} onClick={() => setTab("invite")} testId="pro-tab-invite">
            <UserPlus className="h-3.5 w-3.5" strokeWidth={2} /> Inviter un client
          </TabBtn>
          <TabBtn active={tab === "clients"} onClick={() => setTab("clients")} testId="pro-tab-clients">
            <Users className="h-3.5 w-3.5" strokeWidth={2} /> Mes clients
          </TabBtn>
        </div>

        {tab === "inbox" && (
          items.length === 0 ? (
            <div className="rounded-xl border border-[#E4E1D8] bg-white p-12 text-center shadow-sm">
              <CheckCircle2 className="h-8 w-8 text-[#059669] mx-auto" strokeWidth={1.5} />
              <div className="mt-4 font-display text-lg text-[#1F2937]">Aucune demande dans cette priorité</div>
              <div className="mt-2 text-sm text-[#5B5B62]">Excellent — vous êtes à jour.</div>
            </div>
          ) : (
            <div className="space-y-3 altiora-stagger">
              {items.map((b) => (
                <BookingRow key={b.id} booking={b} onAction={(a) => { setSelected(b); setAction(a); }} />
              ))}
            </div>
          )
        )}

        {tab === "stats" && stats && (
          <StatsPanel stats={stats} pro={pro} />
        )}

        {tab === "profile" && (
          <ProfilePanel pro={pro} />
        )}

        {tab === "invite" && (
          <InviteClientPanel proApi={proApi} pro={pro} />
        )}

        {tab === "clients" && (
          <MyClientsPanel proApi={proApi} />
        )}
      </div>

      {selected && action && (
        <ActionDialog
          booking={selected}
          action={action}
          onClose={() => { setSelected(null); setAction(null); }}
          onDone={async () => { setSelected(null); setAction(null); await load(); }}
        />
      )}
    </div>
  );
}

/* ============ Sub-components ============ */

function BandKpi({ emoji, icon: Icon, label, value, accent, onClick, active, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 altiora-transition ${
        active ? "border-[#0B2545] bg-white shadow-md" : "border-[#E4E1D8] bg-white hover:border-[#B8B5AC] hover:shadow-sm"
      }`}
    >
      <div className={`flex items-center gap-1.5 text-xs ${accent}`}>
        {emoji ? <span className="text-base leading-none">{emoji}</span> : (Icon ? <Icon className="h-3.5 w-3.5" strokeWidth={2} /> : null)}
        <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className={`mt-2 font-display text-2xl ${accent}`}>{value}</div>
    </button>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-mono uppercase tracking-widest altiora-transition ${
        active ? "bg-[#0B2545] text-white" : "text-[#5B5B62] hover:text-[#1F2937]"
      }`}
    >
      {children}
    </button>
  );
}

function BookingRow({ booking, onAction }) {
  const s = BAND_STYLES[booking.criticality.band] || BAND_STYLES.info;
  const MIco = MODALITY_ICON[booking.modality] || Video;
  return (
    <div data-testid={`pro-booking-${booking.id}`} className={`relative rounded-xl border ${s.border} ${s.bg} shadow-sm altiora-transition`}>
      <div className={`absolute left-0 top-4 bottom-4 w-1 rounded-r-full ${s.ring}`}></div>
      <div className="pl-5 pr-5 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${s.chip}`}>
                <span>{booking.criticality.emoji}</span> {booking.criticality.label}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-[#E4E1D8] bg-white/70 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                <MIco className="h-3 w-3" strokeWidth={2} /> {booking.modality}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                Impact <span className="text-[#1F2937] font-medium">{Math.round(booking.criticality.impact_score)}</span>
              </span>
              {booking.criticality.keyword_hits > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#DC2626]/30 bg-[#FEE2E2] px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#991B1B]">
                  <Flame className="h-3 w-3" strokeWidth={2} /> {booking.criticality.keyword_hits} signal(s)
                </span>
              )}
            </div>
            <div className="mt-2 font-display text-base text-[#1F2937] tracking-tight">
              Demande de {booking.user_email?.split("@")[0] || "utilisateur"}
            </div>
            <div className="mt-1.5 text-sm text-[#5B5B62] leading-relaxed">
              « {booking.topic_fr} »
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(booking.proposed_slots || []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-md border border-[#E4E1D8] bg-white px-2 py-0.5 text-[10px] font-mono text-[#5B5B62]">
                  <Clock className="h-3 w-3" strokeWidth={2} />
                  {new Date(s).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              ))}
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
            {new Date(booking.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {booking.status === "pending_pro_confirmation" && (
            <>
              <ActionBtn variant="primary" onClick={() => onAction("confirm")} testId={`action-confirm-${booking.id}`} icon={CheckCircle2}>Confirmer</ActionBtn>
              <ActionBtn variant="outline" onClick={() => onAction("counter")} testId={`action-counter-${booking.id}`} icon={Clock}>Contre-proposer</ActionBtn>
              <ActionBtn variant="ghost" onClick={() => onAction("decline")} testId={`action-decline-${booking.id}`} icon={XCircle}>Décliner</ActionBtn>
            </>
          )}
          {booking.status === "confirmed" && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#059669]/30 bg-[#ECFDF5] px-3 py-1.5 text-xs text-[#065F46] font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> Confirmé · {booking.confirmed_slot ? new Date(booking.confirmed_slot).toLocaleString("fr-FR") : ""}
            </span>
          )}
          {booking.status === "confirmed" && (
            <ActionBtn variant="primary" onClick={() => onAction("report")} testId={`action-report-${booking.id}`} icon={Award}>
              Rapport post-consultation
            </ActionBtn>
          )}
          {booking.status === "completed" && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#0B2545]/30 bg-[#0B2545]/8 px-3 py-1.5 text-xs text-[#0B2545] font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> Rapport soumis
            </span>
          )}
          {booking.status === "counter_proposed" && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#0B2545]/30 bg-[#0B2545]/5 px-3 py-1.5 text-xs text-[#0B2545] font-medium">
              <Clock className="h-3.5 w-3.5" strokeWidth={2} /> Contre-propositions envoyées ({(booking.counter_slots || []).length})
            </span>
          )}
          {booking.status === "declined" && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#DC2626]/30 bg-[#FEF2F2] px-3 py-1.5 text-xs text-[#991B1B] font-medium">
              <XCircle className="h-3.5 w-3.5" strokeWidth={2} /> Décliné
            </span>
          )}
          <ActionBtn variant="ghost" onClick={() => onAction("notes")} testId={`action-notes-${booking.id}`} icon={MessageCircle}>Note privée</ActionBtn>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ variant, onClick, children, icon: Icon, testId }) {
  const base = "inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-1.5 rounded-md altiora-transition";
  const cls = variant === "primary"
    ? "bg-[#0B2545] hover:bg-[#123163] text-white shadow-sm"
    : variant === "outline"
      ? "border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
      : "text-[#5B5B62] hover:text-[#1F2937] hover:bg-white/70";
  return (
    <button data-testid={testId} onClick={onClick} className={`${base} ${cls}`}>
      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2} />}
      {children}
    </button>
  );
}

/* ============ Action Dialog ============ */
function ActionDialog({ booking, action, onClose, onDone }) {
  const [selectedSlot, setSelectedSlot] = useState("");
  const [counterSlots, setCounterSlots] = useState([""]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const titleMap = {
    confirm: "Confirmer le rendez-vous",
    counter: "Proposer d'autres créneaux",
    decline: "Décliner la demande",
    notes: "Ajouter une note interne",
    report: "Rapport post-consultation",
  };

  // Facts state
  const [facts, setFacts] = useState([]);
  const [factOptions, setFactOptions] = useState([]);
  const [factCategory, setFactCategory] = useState("all");
  const [nextStep, setNextStep] = useState("");

  useEffect(() => {
    if (action !== "report") return;
    (async () => {
      try {
        const { data } = await proApi.get("/partner-pro/facts");
        setFactOptions(data.items || []);
      } catch (err) {
        toast.error("Impossible de charger la liste des faits.");
      }
    })();
  }, [action]);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (action === "confirm") {
        if (!selectedSlot) return toast.error("Sélectionnez un créneau.");
        await proApi.post(`/partner-pro/bookings/${booking.id}/confirm`, {
          chosen_slot: selectedSlot,
          note_fr: note || null,
        });
        toast.success("Rendez-vous confirmé.");
      } else if (action === "counter") {
        const validSlots = counterSlots.filter((s) => s && s.trim()).map((s) => new Date(s).toISOString());
        if (validSlots.length === 0) return toast.error("Ajoutez au moins 1 créneau alternatif.");
        await proApi.post(`/partner-pro/bookings/${booking.id}/counter`, {
          counter_slots: validSlots,
          note_fr: note || null,
        });
        toast.success("Contre-propositions envoyées.");
      } else if (action === "decline") {
        if (reason.length < 5) return toast.error("Motif ≥ 5 caractères.");
        await proApi.post(`/partner-pro/bookings/${booking.id}/decline`, {
          reason_fr: reason,
        });
        toast.success("Demande déclinée.");
      } else if (action === "notes") {
        if (note.length < 1) return toast.error("Note vide.");
        await proApi.post(`/partner-pro/bookings/${booking.id}/notes`, { note_fr: note });
        toast.success("Note enregistrée.");
      } else if (action === "report") {
        if (facts.length === 0) return toast.error("Ajoutez au moins 1 fait à rapporter.");
        const r = await proApi.post(`/partner-pro/bookings/${booking.id}/report`, {
          booking_completed: true,
          facts: facts.map((k) => ({ fact_key: k })),
          next_step_fr: nextStep || null,
          private_notes_fr: note || null,
        });
        const applied = (r.data.fact_results || []).filter((f) => f.applied_auto).length;
        const pending = (r.data.fact_results || []).filter((f) => f.pending_validation).length;
        toast.success(`Rapport transmis · ${applied} impact(s) appliqué(s), ${pending} en validation utilisateur.`);
      }
      onDone();
    } catch {
      toast.error("Erreur — réessayez.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="pro-action-dialog">
      <div className="absolute inset-0 bg-[#1F2937]/50 backdrop-blur-sm altiora-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl border border-[#E4E1D8] shadow-2xl altiora-scale-in max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-[#E4E1D8] flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">Action pro</div>
            <div className="mt-1 font-display text-lg text-[#1F2937]">{titleMap[action]}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[#5B5B62] hover:text-[#1F2937]">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] p-3 text-sm text-[#5B5B62]">
            « {booking.topic_fr} »
          </div>

          {action === "confirm" && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-2">
                Choisissez un créneau proposé
              </div>
              <div className="grid grid-cols-1 gap-2">
                {(booking.proposed_slots || []).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedSlot(s)}
                    data-testid={`confirm-slot-${s}`}
                    className={`text-left rounded-md border px-3 py-2.5 altiora-transition ${
                      selectedSlot === s ? "bg-[#0B2545]/8 border-[#0B2545]" : "bg-white border-[#E4E1D8] hover:border-[#B8B5AC]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className={`h-3.5 w-3.5 ${selectedSlot === s ? "text-[#0B2545]" : "text-[#8B8B8F]"}`} strokeWidth={2} />
                      <span className="text-sm text-[#1F2937]">
                        {new Date(s).toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {action === "counter" && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-2">
                Proposez 1 à 3 créneaux alternatifs
              </div>
              <div className="space-y-2">
                {counterSlots.map((v, i) => (
                  <input
                    key={i}
                    type="datetime-local"
                    value={v}
                    onChange={(e) => setCounterSlots((cs) => cs.map((x, idx) => idx === i ? e.target.value : x))}
                    className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm focus:outline-none focus:border-[#0B2545]"
                  />
                ))}
                {counterSlots.length < 3 && (
                  <button
                    onClick={() => setCounterSlots((cs) => [...cs, ""])}
                    className="text-xs text-[#0B2545] hover:text-[#123163] font-mono uppercase tracking-widest"
                  >
                    + Ajouter un créneau
                  </button>
                )}
              </div>
            </div>
          )}

          {action === "decline" && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-2">
                Motif de refus
              </div>
              <textarea
                data-testid="decline-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Ex : Agenda saturé, spécialité non alignée…"
                className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm focus:outline-none focus:border-[#0B2545]"
              />
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                Le motif est partagé avec l'utilisateur.
              </div>
            </div>
          )}

          {action === "report" && (
            <div className="space-y-3">
              <div className="rounded-md border border-[#C5A880]/40 bg-[#FBF6EA] p-3 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 text-[#7A5B14] flex-shrink-0" strokeWidth={2} />
                <div className="text-[11px] text-[#7A5B14] leading-relaxed">
                  Vous soumettez des <strong>faits</strong>. ALTIORA en déduit l'impact sur le score
                  selon ses propres règles. Un fait à haute confiance (≥85 %) met à jour le score
                  automatiquement ; sinon, l'utilisateur valide.
                </div>
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap gap-1">
                {["all", "career", "finance", "immigration", "education", "wellbeing", "diagnostic", "soft"].map((c) => (
                  <button
                    key={c}
                    onClick={() => setFactCategory(c)}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border altiora-transition ${
                      factCategory === c ? "bg-[#0B2545] border-[#0B2545] text-white" : "border-[#E4E1D8] bg-white text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >{c}</button>
                ))}
              </div>

              <div className="rounded-md border border-[#E4E1D8] bg-white max-h-[240px] overflow-y-auto divide-y divide-[#F1EFE9]">
                {factOptions
                  .filter((f) => factCategory === "all" || f.category === factCategory)
                  .map((f) => {
                    const active = facts.includes(f.key);
                    return (
                      <label key={f.key} className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-[#F8F7F3]">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => setFacts((cur) => active ? cur.filter((x) => x !== f.key) : [...cur, f.key])}
                          data-testid={`fact-${f.key}`}
                          className="h-4 w-4 mt-0.5 accent-[#0B2545] flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-sm text-[#1F2937]">{f.label_fr}</div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                            {f.category} · confiance de base {f.base_confidence}%
                          </div>
                        </div>
                      </label>
                    );
                  })}
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-2">
                  Prochaine étape recommandée
                </div>
                <input
                  data-testid="report-next-step"
                  value={nextStep}
                  onChange={(e) => setNextStep(e.target.value)}
                  placeholder="Ex : Revoir dans 30 jours pour ajuster la stratégie…"
                  className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm focus:outline-none focus:border-[#0B2545]"
                />
              </div>
            </div>
          )}

          {(action === "confirm" || action === "counter" || action === "notes") && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-2">
                {action === "notes" ? "Note interne (privée)" : "Message pour l'utilisateur (optionnel)"}
              </div>
              <textarea
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm focus:outline-none focus:border-[#0B2545]"
              />
            </div>
          )}
        </div>

        <div className="border-t border-[#E4E1D8] bg-[#F8F7F3] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-[#5B5B62] hover:text-[#1F2937] px-4 py-2">
            Annuler
          </button>
          <button
            data-testid="pro-dialog-submit"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition shadow-sm disabled:opacity-60"
          >
            {submitting ? "Envoi…" : "Envoyer"}
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ Stats + Profile panels ============ */
function StatsPanel({ stats, pro }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <StatCard icon={Users} label="Total demandes" value={stats.total_bookings} sub={`${stats.confirmed} confirmées`} />
      <StatCard icon={TrendingUp} label="Taux de conversion" value={`${stats.conversion_rate}%`} sub="Confirmées / demandes" />
      <StatCard icon={DollarSign} label="Revenus estimés" value={`${stats.revenue_estimate_cad} $`} sub="Basé sur tarif séance" />
      <StatCard icon={Star} label="Note moyenne" value={(stats.avg_rating || pro.rating || 0).toFixed(2)} sub={`${stats.ratings_count} avis`} />
      <div className="md:col-span-2 rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-3">Répartition des statuts</div>
        <div className="space-y-2">
          {Object.entries(stats.by_status || {}).map(([status, count]) => (
            <div key={status} className="flex items-center justify-between text-sm">
              <span className="text-[#5B5B62]">{status.replace(/_/g, " ")}</span>
              <span className="text-[#1F2937] font-medium">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-[#0B2545]">
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        <div className="font-mono text-[10px] uppercase tracking-widest">{label}</div>
      </div>
      <div className="mt-2 font-display text-3xl text-[#1F2937]">{value}</div>
      <div className="mt-1 text-[11px] text-[#8B8B8F]">{sub}</div>
    </div>
  );
}

function ProfilePanel({ pro }) {
  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 shadow-sm">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-4">Profil professionnel</div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Prénom" value={pro.first_name} />
        <Field label="Nom" value={pro.last_name} />
        <Field label="Titre" value={pro.title_fr} />
        <Field label="Spécialité" value={pro.specialty} />
        <Field label="Cabinet" value={pro.org_name} />
        <Field label="Ville" value={pro.city} />
        <Field label="Rating" value={`${pro.rating?.toFixed(2) || "—"} · ${pro.reviews_count || 0} avis`} />
        <Field label="Vérifié" value={pro.verified ? "Oui" : "Non"} />
        <Field label="Session expire le" value={new Date(pro.session_expires_at).toLocaleString("fr-FR")} />
      </div>
      <div className="mt-4 flex items-start gap-2 text-[11px] text-[#5B5B62]">
        <Info className="h-3 w-3 mt-0.5 text-[#0B2545] flex-shrink-0" strokeWidth={2} />
        <span>La modification de profil arrivera en Phase 2. Contactez ALTIORA pour toute mise à jour urgente.</span>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className="mt-1 text-[#1F2937]">{value || "—"}</div>
    </div>
  );
}


/* ============ Invite Client Panel (P1 · Pro Consultation Flow) ============ */
function InviteClientPanel({ proApi, pro }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copiedToken, setCopiedToken] = useState("");

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [location, setLocation] = useState(null);

  const publicBase = (typeof window !== "undefined" ? window.location.origin : "");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await proApi.get("/partner-pro/consultation/list");
      setItems(data.items || []);
    } catch (err) {
      toast.error("Impossible de charger vos invitations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim() || !firstName.trim()) {
      toast.error("Email et prénom du client requis");
      return;
    }
    setSending(true);
    try {
      await proApi.post("/partner-pro/consultation/invite", {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        personal_message: personalMessage.trim(),
        location_label: location?.label || "",
        location_country: location?.country || "",
      });
      toast.success("Invitation envoyée par courriel");
      setEmail(""); setFirstName(""); setLastName(""); setPersonalMessage(""); setLocation(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Envoi échoué";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc.slice(-1)[0] : "";
        msg = `${field ? field + " : " : ""}${first?.msg || "champ invalide"}`;
      }
      toast.error(msg, { duration: 8000 });
    } finally { setSending(false); }
  };

  const copyLink = async (token) => {
    const link = `${publicBase}/consult?token=${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(token);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopiedToken(""), 2500);
    } catch {
      toast.error("Copie impossible — sélectionnez manuellement");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6" data-testid="pro-invite-panel">
      <form
        onSubmit={send}
        className="rounded-xl border border-[#E4E1D8] bg-white p-6 shadow-sm space-y-4"
        data-testid="pro-invite-form"
      >
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
          <div className="font-display text-lg text-[#1F2937] tracking-tight">Inviter un client à ALTIORA</div>
        </div>
        <p className="text-sm text-[#5B5B62] leading-relaxed">
          Envoyez à votre client un lien conversationnel unique. Il ouvrira son espace ALTIORA
          rattaché à vous, complètera son diagnostic IMES&trade;, et démarrera son accompagnement.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Prénom</div>
            <input
              data-testid="pro-invite-firstname"
              type="text" required
              value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Nom (facultatif)</div>
            <input
              data-testid="pro-invite-lastname"
              type="text"
              value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
            />
          </label>
        </div>

        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Courriel du client</div>
          <input
            data-testid="pro-invite-email"
            type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@exemple.com"
            className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
          />
        </label>

        <div>
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] flex items-center gap-1.5">
            <Globe className="h-3 w-3" strokeWidth={2} />
            Localisation du client (facultatif &middot; OSM)
          </div>
          {location ? (
            <div className="flex items-center gap-2 rounded-md border border-[#0B2545]/30 bg-[#0B2545]/5 px-3 py-2.5">
              <MapPin className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={2} />
              <div className="text-sm text-[#1F2937] flex-1 truncate">{location.label}</div>
              <button
                type="button"
                onClick={() => setLocation(null)}
                data-testid="pro-invite-location-clear"
                className="text-[#8B8B8F] hover:text-[#DC2626]"
                aria-label="Effacer"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <GlobalLocationSearch
              lang="fr"
              placeholder="Ville, pays… (Casablanca, Singapore, Paris)"
              onSelect={(sel) => setLocation({ label: sel.label, country: sel.country || "" })}
            />
          )}
        </div>

        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Message personnel (facultatif &middot; max 800 car.)</div>
          <textarea
            data-testid="pro-invite-message"
            value={personalMessage}
            onChange={(e) => setPersonalMessage(e.target.value.slice(0, 800))}
            rows={4}
            placeholder="Ravi de vous accompagner sur votre transition…"
            className="w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm resize-none"
          />
          <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">{personalMessage.length} / 800</div>
        </label>

        <button
          type="submit"
          disabled={sending}
          data-testid="pro-invite-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-3 text-sm font-medium transition-colors"
        >
          {sending ? (
            <>Envoi en cours…</>
          ) : (
            <><Send className="h-4 w-4" strokeWidth={2} /> Envoyer l&apos;invitation</>
          )}
        </button>
        <div className="text-[10px] text-[#8B8B8F] font-mono leading-relaxed">
          Le client recevra un email conversationnel signé &laquo; {pro.first_name} {pro.last_name} &raquo;.
          Aucun mot de passe n&apos;est communiqué : le client crée le sien.
        </div>
      </form>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
            Vos invitations récentes ({items?.length ?? "…"})
          </div>
          <button onClick={load} className="text-[#5B5B62] hover:text-[#0B2545]" title="Rafraîchir">
            <RefreshCcw className="h-3.5 w-3.5" strokeWidth={1.8} />
          </button>
        </div>

        {loading && (
          <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 text-sm text-[#5B5B62]">Chargement…</div>
        )}

        {!loading && items && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-[#E4E1D8] bg-white p-8 text-center">
            <UserPlus className="h-6 w-6 text-[#B8B5AC] mx-auto" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-[#5B5B62]">Aucune invitation envoyée pour l&apos;instant.</div>
            <div className="mt-1 text-[11px] text-[#8B8B8F]">Envoyez la première à gauche &#8598;</div>
          </div>
        )}

        {!loading && items && items.length > 0 && (
          <div className="space-y-2" data-testid="pro-invite-list">
            {items.map((it) => {
              const isAccepted = it.status === "accepted";
              return (
                <div
                  key={it.token}
                  className={`rounded-xl border ${isAccepted ? "border-[#059669]/40 bg-[#ECFDF5]" : "border-[#E4E1D8] bg-white"} p-4 shadow-sm`}
                  data-testid={`pro-invite-item-${it.token}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-sm text-[#1F2937] tracking-tight truncate">
                          {it.first_name || it.email.split("@")[0]} {it.last_name || ""}
                        </div>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                          isAccepted ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FBF6EA] text-[#7A5B14]"
                        }`}>
                          {isAccepted ? "Compte créé" : "En attente"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-[#5B5B62] truncate">{it.email}</div>
                      {it.location_label && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-[#8B8B8F]">
                          <MapPin className="h-3 w-3" strokeWidth={2} /> {it.location_label}
                        </div>
                      )}
                    </div>
                    {!isAccepted && (
                      <button
                        onClick={() => copyLink(it.token)}
                        data-testid={`pro-invite-copy-${it.token}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest text-[#5B5B62] hover:text-[#0B2545] transition-colors shrink-0"
                        title="Copier le lien d'invitation"
                      >
                        {copiedToken === it.token ? <Check className="h-3 w-3 text-[#059669]" strokeWidth={2} /> : <Copy className="h-3 w-3" strokeWidth={2} />}
                        {copiedToken === it.token ? "Copié" : "Lien"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ My Clients Panel (P1+ · post-signup Pro visibility) ============ */
function MyClientsPanel({ proApi }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // full client detail response
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await proApi.get("/partner-pro/clients");
      setItems(data.items || []);
    } catch (err) {
      toast.error("Impossible de charger vos clients");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadList(); }, []);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const { data } = await proApi.get(`/partner-pro/clients/${userId}`);
      setDetail(data);
    } catch (err) {
      toast.error("Impossible de charger ce client");
    } finally { setDetailLoading(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-CA", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  const statusChip = (status, analysis) => {
    const map = {
      not_started: { label: "Non commencé", bg: "bg-[#F1EFE8]", fg: "text-[#5B5B62]" },
      draft: { label: "Ébauche", bg: "bg-[#FBF6EA]", fg: "text-[#7A5B14]" },
      in_progress: { label: "En cours", bg: "bg-[#FBF6EA]", fg: "text-[#7A5B14]" },
      completed: { label: "Complété", bg: "bg-[#D1FAE5]", fg: "text-[#065F46]" },
      abandoned: { label: "Abandonné", bg: "bg-[#F1EFE8]", fg: "text-[#8B8B8F]" },
    };
    const s = map[status] || map.not_started;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${s.bg} ${s.fg}`}>
        {s.label}
        {analysis === "running" && <Loader2Inline />}
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="pro-clients-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-2xl text-[#1F2937] tracking-tight">Mes clients</div>
          <div className="mt-1 text-sm text-[#5B5B62]">
            Clients ayant accepté votre invitation. Les données détaillées apparaissent après leur consentement explicite.
          </div>
        </div>
        <button onClick={loadList} className="text-[#5B5B62] hover:text-[#0B2545] p-2" title="Rafraîchir" data-testid="pro-clients-refresh">
          <RefreshCcw className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>

      {loading && (
        <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 text-sm text-[#5B5B62]">Chargement…</div>
      )}

      {!loading && items && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[#E4E1D8] bg-white p-10 text-center">
          <Users className="h-8 w-8 text-[#B8B5AC] mx-auto" strokeWidth={1.5} />
          <div className="mt-4 font-display text-lg text-[#1F2937]">Aucun client pour l&apos;instant</div>
          <div className="mt-2 text-sm text-[#5B5B62] max-w-md mx-auto leading-relaxed">
            Vos clients apparaîtront ici dès qu&apos;ils auront accepté votre invitation. Envoyez-en une depuis l&apos;onglet <strong>Inviter un client</strong>.
          </div>
        </div>
      )}

      {!loading && items && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pro-clients-list">
          {items.map((it) => {
            const diag = it.diagnostic;
            const initials = ((it.first_name || "?")[0] + (it.last_name || "")[0]).toUpperCase();
            return (
              <button
                key={it.user_id}
                onClick={() => openDetail(it.user_id)}
                data-testid={`pro-client-card-${it.user_id}`}
                className="text-left rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm hover:border-[#0B2545] transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 h-11 w-11 rounded-full bg-[#0B2545] text-white flex items-center justify-center font-display text-sm tracking-tight">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-[#1F2937] tracking-tight truncate">
                      {it.name || it.email.split("@")[0]}
                    </div>
                    <div className="text-xs text-[#5B5B62] truncate">{it.email}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {diag ? statusChip(diag.status, diag.analysis_status) : statusChip("not_started")}
                      {diag?.imes_score != null && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#0B2545]/10 text-[#0B2545] px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest">
                          IMES {Number(diag.imes_score).toFixed(0)}
                          {diag.imes_grade && <> &middot; {diag.imes_grade}</>}
                        </span>
                      )}
                      {!it.share_consent && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF2E7] text-[#7A5B14] px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest">
                          <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2} /> Consentement requis
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-[#8B8B8F]">
                      Client depuis {fmtDate(it.created_at)}
                      {diag?.updated_at && <> &middot; MAJ {fmtDate(diag.updated_at)}</>}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#B8B5AC] group-hover:text-[#0B2545] group-hover:translate-x-0.5 transition-all" strokeWidth={1.8} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {detail && (
        <ClientDetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
          proApi={proApi}
          onRefresh={loadList}
        />
      )}
    </div>
  );
}

function Loader2Inline() {
  return <RefreshCcw className="h-2.5 w-2.5 animate-spin ml-0.5" strokeWidth={2} />;
}

function ClientDetailModal({ detail, loading, onClose, proApi, onRefresh }) {
  const u = detail?.user || {};
  const diag = detail?.diagnostic || null;
  const scores = diag?.scores || {};
  const ai = diag?.ai_analysis || null;
  const scenarios = diag?.scenarios || [];
  const [clientDossiers, setClientDossiers] = useState([]);
  const [showDossierForm, setShowDossierForm] = useState(false);

  useEffect(() => {
    if (!u.user_id) return;
    (async () => {
      try {
        const { data } = await proApi.get(`/partner-pro/clients/${u.user_id}/dossiers`);
        setClientDossiers(data.items || []);
      } catch { /* silent */ }
    })();
  }, [u.user_id]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pro-client-detail-modal"
    >
      <div
        className="bg-[#F8F7F3] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#F8F7F3]/95 backdrop-blur border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Client ALTIORA</div>
            <div className="font-display text-xl text-[#1F2937] tracking-tight">{u.name || u.email}</div>
            <div className="text-xs text-[#5B5B62]">{u.email}</div>
          </div>
          <button onClick={onClose} className="text-[#5B5B62] hover:text-[#DC2626]" aria-label="Fermer" data-testid="pro-client-detail-close">
            <X className="h-5 w-5" strokeWidth={1.8} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading && <div className="text-sm text-[#5B5B62]">Chargement…</div>}

          {/* Pro-side: dossiers for this client */}
          {!loading && (
            <ProClientDossiersSection
              proApi={proApi}
              user={u}
              dossiers={clientDossiers}
              onRefresh={async () => {
                try {
                  const { data } = await proApi.get(`/partner-pro/clients/${u.user_id}/dossiers`);
                  setClientDossiers(data.items || []);
                } catch { /* silent */ }
              }}
              show={showDossierForm}
              setShow={setShowDossierForm}
            />
          )}

          {!loading && detail?.share_consent === false && (
            <div className="rounded-xl border border-[#C5A880]/40 bg-[#FBF6EA] p-5" data-testid="pro-client-consent-notice">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-[#7A5B14] mt-0.5" strokeWidth={1.8} />
                <div>
                  <div className="font-display text-base text-[#1F2937] tracking-tight">Consentement en attente</div>
                  <p className="mt-2 text-sm text-[#5B5B62] leading-relaxed">
                    {detail.message_fr || "Votre client n'a pas encore autorisé le partage de son diagnostic avec vous."}
                  </p>
                  <p className="mt-2 text-xs text-[#8B8B8F] leading-relaxed">
                    Invitez-le à activer le partage depuis ses paramètres personnels ; jusqu&apos;à cet accord, seuls
                    son nom et le statut global de son diagnostic vous sont visibles.
                  </p>
                  {detail.diagnostic_summary && (
                    <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                      Statut du diagnostic : {detail.diagnostic_summary.status || "non commencé"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!loading && detail?.share_consent === true && !diag && (
            <div className="rounded-xl border border-[#E4E1D8] bg-white p-6 text-center">
              <Sparkles className="h-6 w-6 text-[#B8B5AC] mx-auto" strokeWidth={1.5} />
              <div className="mt-3 font-display text-lg text-[#1F2937]">Diagnostic non commencé</div>
              <div className="mt-1 text-sm text-[#5B5B62]">Votre client n&apos;a pas encore ouvert son diagnostic IMES&trade;.</div>
            </div>
          )}

          {!loading && detail?.share_consent === true && diag && (
            <>
              {/* Scores */}
              <div className="rounded-xl border border-[#E4E1D8] bg-white p-5" data-testid="pro-client-scores">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">Score IMES&trade;</div>
                    <div className="mt-1 font-display text-4xl text-[#0B2545] tracking-tight">
                      {scores.imes != null ? Number(scores.imes).toFixed(0) : "—"}
                      {scores.imes_grade && <span className="text-2xl text-[#7A5B14] ml-2">{scores.imes_grade}</span>}
                    </div>
                  </div>
                  <div className="text-right text-xs text-[#5B5B62]">
                    <div>Statut : {diag.status}</div>
                    <div>Méthode : {diag.method_version || "v2"}</div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["patrimoine","emploi","employabilite","education","entrepreneuriat","reseau","resilience","autonomie"].map((k) => (
                    <div key={k} className="rounded-md bg-[#F8F7F3] p-3">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B8B8F]">{k}</div>
                      <div className="mt-1 font-display text-lg text-[#1F2937]">{scores[k] != null ? Number(scores[k]).toFixed(0) : "—"}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Analysis */}
              {ai && (
                <div className="rounded-xl border border-[#E4E1D8] bg-white p-5 space-y-4" data-testid="pro-client-ai-analysis">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">Analyse IA</div>
                  {ai.strengths_fr?.length > 0 && (
                    <AnalysisList title="Forces" items={ai.strengths_fr} accent="text-[#065F46]" />
                  )}
                  {ai.vulnerabilities_fr?.length > 0 && (
                    <AnalysisList title="Vulnérabilités" items={ai.vulnerabilities_fr} accent="text-[#7A5B14]" />
                  )}
                  {ai.priorities_fr?.length > 0 && (
                    <AnalysisList title="Priorités" items={ai.priorities_fr} accent="text-[#0B2545]" />
                  )}
                  {ai.recommendations_fr?.length > 0 && (
                    <AnalysisList title="Recommandations" items={ai.recommendations_fr} accent="text-[#0B2545]" />
                  )}
                </div>
              )}

              {/* Scenarios */}
              {scenarios.length > 0 && (
                <div className="rounded-xl border border-[#E4E1D8] bg-white p-5 space-y-3" data-testid="pro-client-scenarios">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">Scénarios prospectifs</div>
                  {scenarios.map((s, i) => (
                    <div key={i} className="border-l-2 border-[#0B2545] pl-4 py-1">
                      <div className="font-display text-sm text-[#1F2937] tracking-tight">{s.label_fr}</div>
                      <div className="text-xs text-[#5B5B62] mt-1 leading-relaxed">{s.narrative_fr}</div>
                      <div className="mt-2 text-[10px] font-mono text-[#8B8B8F]">
                        Horizon : {s.horizon_years} ans &middot; Revenu projeté : {Number(s.projected_income_cad).toLocaleString("fr-CA")} $ &middot; Autonomie : {Number(s.autonomy_index).toFixed(0)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalysisList({ title, items, accent }) {
  return (
    <div>
      <div className={`text-xs font-medium ${accent} mb-1.5`}>{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-[#374151] leading-relaxed flex items-start gap-2">
            <span className={`mt-1.5 h-1 w-1 rounded-full ${accent.replace("text-", "bg-")} flex-shrink-0`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}



/* ============ Pro · Create/List Dossiers for a client ============ */
function ProClientDossiersSection({ proApi, user, dossiers, onRefresh, show, setShow }) {
  return (
    <div className="rounded-xl border border-[#0B2545]/20 bg-white p-5" data-testid="pro-client-dossiers-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">Dossiers ALTIORA</div>
          <div className="font-display text-base text-[#1F2937] tracking-tight mt-0.5">
            {dossiers.length} dossier{dossiers.length > 1 ? "s" : ""} pour ce client
          </div>
        </div>
        <button
          onClick={() => setShow((s) => !s)}
          data-testid="pro-open-create-dossier"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-3.5 py-1.5 text-xs font-medium transition-colors"
        >
          {show ? <><X className="h-3 w-3" strokeWidth={2} /> Annuler</> : <><UserPlus className="h-3 w-3" strokeWidth={2} /> Créer un dossier</>}
        </button>
      </div>

      {dossiers.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="pro-client-dossiers-list">
          {dossiers.map((d) => (
            <div key={d.dossier_id} className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-[#1F2937] truncate">{d.name}</div>
                <div className="mt-0.5 text-[10px] font-mono text-[#5B5B62]">
                  {d.type === "individual" ? "Personne" : "Organisation"}
                  {d.subtype && <> · {d.subtype}</>}
                  {" · "}<span className={`${d.status === "active" ? "text-[#065F46]" : d.status === "pending_client_approval" ? "text-[#7A5B14]" : "text-[#8B8B8F]"}`}>{d.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && (
        <CreateClientDossierForm
          proApi={proApi}
          user={user}
          onDone={async () => { setShow(false); await onRefresh(); }}
        />
      )}
    </div>
  );
}

function CreateClientDossierForm({ proApi, user, onDone }) {
  const [type, setType] = useState("individual");
  const [name, setName] = useState(user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "");
  const [subtype, setSubtype] = useState("");
  const [eliaModule, setEliaModule] = useState("");
  const [mode, setMode] = useState("draft");
  const [legalName, setLegalName] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [saving, setSaving] = useState(false);

  const SUBTYPES = {
    individual: ["", "self", "enfant", "conjoint", "beneficiary", "patrimoine_famille"],
    legal_entity: ["", "entreprise", "actionnaire"],
  };
  const ELIA_MODULES = ["", "family", "business", "career", "finance", "student", "institution", "transitions", "trajectory"];

  const submit = async () => {
    if (!name.trim() && type === "individual") { toast.error("Nom requis"); return; }
    if (type === "legal_entity" && !legalName.trim()) { toast.error("Raison sociale requise"); return; }
    setSaving(true);
    try {
      await proApi.post(`/partner-pro/clients/${user.user_id}/dossiers`, {
        type,
        name: type === "legal_entity" ? legalName.trim() : name.trim(),
        mode,
        subtype: subtype || undefined,
        elia_module: eliaModule || undefined,
        first_name: type === "individual" ? user.first_name : undefined,
        last_name: type === "individual" ? user.last_name : undefined,
        legal_name: type === "legal_entity" ? legalName.trim() : undefined,
        business_number: type === "legal_entity" ? businessNumber.trim() || undefined : undefined,
      });
      toast.success(mode === "draft"
        ? "Dossier créé en attente d'approbation client."
        : "Dossier créé et activé."
      );
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Création impossible");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 rounded-xl border border-[#E4E1D8] bg-[#F8F7F3] p-5 space-y-4" data-testid="pro-create-dossier-form">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545]">
        Nouveau dossier pour {user.name || user.email}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Type</div>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="pro-create-type"
            className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm">
            <option value="individual">Personne physique</option>
            <option value="legal_entity">Personne morale</option>
          </select>
        </label>
        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Mode</div>
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="pro-create-mode"
            className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm">
            <option value="draft">Brouillon (approbation client requise)</option>
            <option value="direct">Direct (mandat signé)</option>
          </select>
        </label>
      </div>

      {type === "individual" ? (
        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Nom du dossier</div>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="pro-create-name"
            className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm" />
        </label>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Raison sociale</div>
            <input type="text" value={legalName} onChange={(e) => setLegalName(e.target.value)} data-testid="pro-create-legalname"
              className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">NEQ (facultatif)</div>
            <input type="text" value={businessNumber} onChange={(e) => setBusinessNumber(e.target.value)} data-testid="pro-create-neq"
              className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm" />
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Sous-type (facultatif)</div>
          <select value={subtype} onChange={(e) => setSubtype(e.target.value)} data-testid="pro-create-subtype"
            className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm">
            {SUBTYPES[type].map((s) => <option key={s} value={s}>{s || "—"}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Module ALTIORA (facultatif)</div>
          <select value={eliaModule} onChange={(e) => setEliaModule(e.target.value)} data-testid="pro-create-elia"
            className="w-full rounded-md bg-white border border-[#E4E1D8] px-3 py-2 text-sm">
            {ELIA_MODULES.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
          </select>
        </label>
      </div>

      <div className="rounded-md border border-[#C5A880]/40 bg-[#FBF6EA] p-3 flex items-start gap-2 text-[11px] text-[#7A5B14] leading-relaxed">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} />
        <div>
          {mode === "draft"
            ? "Le client verra ce dossier en attente d'approbation dans son espace ALTIORA. Il devra l'activer avant que vous puissiez collaborer dessus."
            : "Le dossier sera actif immédiatement. Utilisez ce mode uniquement lorsque vous disposez d'un mandat signé du client."}
        </div>
      </div>

      <button
        onClick={submit}
        disabled={saving}
        data-testid="pro-create-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors"
      >
        {saving ? "Création…" : "Créer le dossier"}
        <ChevronRight className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );
}
